import { Workflow, WorkflowExecution, PaginatedResponse } from '../types';
import { apiClient, API_ENDPOINTS, handleApiError } from './api';

export const workflowsService = {
  async getWorkflows(workspace_id: string, page: number = 1, per_page: number = 20): Promise<PaginatedResponse<Workflow>> {
    try {
      console.log('Fetching workflows from backend API...');
      
      const response = await apiClient.get(API_ENDPOINTS.WORKFLOWS.LIST, {
        params: {
          workspace_id,
          page,
          per_page,
          is_active: true // Only get active workflows
        }
      });
      
      return {
        items: response.data.workflows,
        total: response.data.total,
        page: response.data.page,
        size: response.data.per_page,
        pages: response.data.pages
      };
    } catch (error) {
      console.error('Error fetching workflows:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async getWorkflow(id: string): Promise<Workflow> {
    try {
      console.log(`Fetching workflow ${id} from backend API...`);
      
      const response = await apiClient.get(API_ENDPOINTS.WORKFLOWS.GET(parseInt(id)));
      
      return response.data;
    } catch (error) {
      console.error('Error fetching workflow:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async createWorkflow(data: {
    name: string;
    description?: string;
    workspace_id: string;
    pattern?: string;
    orchestrator_name?: string;
    steps: Array<{
      order: number;
      agent_id: number;
      timeout?: number;
      conditions?: Record<string, any>;
      retry_count?: number;
    }>;
    triggers?: string[];
    settings?: Record<string, any>;
    is_active?: boolean;
  }): Promise<Workflow> {
    try {
      console.log('Creating workflow via backend API...');
      
      const response = await apiClient.post(API_ENDPOINTS.WORKFLOWS.CREATE, data);
      
      return response.data;
    } catch (error) {
      console.error('Error creating workflow:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async updateWorkflow(id: string, data: Partial<{
    name: string;
    description: string;
    workspace_id: string;
    pattern: string;
    orchestrator_name: string;
    steps: Array<{
      order: number;
      agent_id: number;
      timeout?: number;
      conditions?: Record<string, any>;
      retry_count?: number;
    }>;
    triggers: string[];
    settings: Record<string, any>;
    is_active: boolean;
  }>): Promise<Workflow> {
    try {
      console.log(`Updating workflow ${id} via backend API...`);
      
      const response = await apiClient.put(API_ENDPOINTS.WORKFLOWS.UPDATE(parseInt(id)), data);
      
      return response.data;
    } catch (error) {
      console.error('Error updating workflow:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async deleteWorkflow(id: string): Promise<void> {
    try {
      console.log(`Deleting workflow ${id} via backend API...`);
      
      await apiClient.delete(API_ENDPOINTS.WORKFLOWS.DELETE(parseInt(id)));
      
    } catch (error) {
      console.error('Error deleting workflow:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async executeWorkflow(id: string, input: Record<string, any>): Promise<WorkflowExecution> {
    try {
      console.log(`Executing workflow ${id}...`);
      
      const response = await apiClient.post(API_ENDPOINTS.WORKFLOWS.EXECUTE(parseInt(id)), input);
      
      return response.data;
    } catch (error) {
      console.error('Error executing workflow:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  // Streaming workflow execution with Server-Sent Events
  async executeWorkflowStreaming(
    id: string,
    executionData: {
      input_data: string;
      context_variables?: Record<string, any>;
      max_turns?: number;
    },
    onEvent: (event: any) => void,
    onError: (error: any) => void,
    onComplete: () => void
  ): Promise<void> {
    try {
      console.log(`Starting streaming execution for workflow ${id}...`);
      
      // Get auth token from storage
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${apiClient.defaults.baseURL}/workflows/${id}/execute-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify(executionData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body reader available');
      }

      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            onComplete();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const eventData = JSON.parse(line.slice(6)); // Remove 'data: ' prefix
                onEvent(eventData);
                
                // Check for completion or error events
                if (eventData.type === 'execution_completed' || eventData.type === 'workflow_completed') {
                  onComplete();
                  return;
                } else if (eventData.type === 'execution_error' || eventData.type === 'execution_failed') {
                  onError(new Error(eventData.error || 'Workflow execution failed'));
                  return;
                }
              } catch (parseError) {
                console.error('Failed to parse event data:', parseError, line);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

    } catch (error) {
      console.error('Error in streaming workflow execution:', error);
      onError(error);
    }
  },

  // Send user response during workflow execution
  async sendUserResponse(
    executionId: string,
    response: string,
    context?: Record<string, any>
  ): Promise<void> {
    try {
      console.log(`Sending user response for execution ${executionId}...`);
      
      await apiClient.post(`/workflows/executions/${executionId}/respond`, {
        response,
        context: context || {}
      });
      
    } catch (error) {
      console.error('Error sending user response:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async getWorkflowExecutions(workflowId: string, page: number = 1, size: number = 10): Promise<PaginatedResponse<WorkflowExecution>> {
    try {
      console.log(`Fetching executions for workflow ${workflowId} from backend API...`);
      
      const response = await apiClient.get(API_ENDPOINTS.WORKFLOWS.EXECUTIONS(parseInt(workflowId)), {
        params: {
          page,
          size
        }
      });
      
      return {
        items: response.data.executions,
        total: response.data.total,
        page: response.data.page,
        size: response.data.per_page,
        pages: response.data.pages
      };
    } catch (error) {
      console.error('Error fetching workflow executions:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async getWorkflowExecution(executionId: string): Promise<WorkflowExecution> {
    try {
      console.log(`Fetching execution ${executionId} from backend API...`);
      
      const response = await apiClient.get(API_ENDPOINTS.WORKFLOWS.EXECUTION(executionId));
      
      return response.data;
    } catch (error) {
      console.error('Error fetching workflow execution:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async cancelWorkflowExecution(executionId: string): Promise<void> {
    try {
      console.log(`Cancelling execution ${executionId} via backend API...`);
      
      await apiClient.post(API_ENDPOINTS.WORKFLOWS.CANCEL_EXECUTION(executionId));
      
    } catch (error) {
      console.error('Error cancelling workflow execution:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async getWorkflowStatistics(): Promise<{
    total_workflows: number;
    active_workflows: number;
    total_executions: number;
    successful_executions: number;
    avg_execution_time: number;
  }> {
    try {
      console.log('Fetching workflow statistics from backend API...');
      
      const response = await apiClient.get(API_ENDPOINTS.WORKFLOWS.STATISTICS);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching workflow statistics:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async searchWorkflows(query: string): Promise<Workflow[]> {
    try {
      console.log('Searching workflows via backend API...');
      
      const response = await apiClient.get(API_ENDPOINTS.WORKFLOWS.SEARCH, {
        params: {
          query
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error searching workflows:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  }
}; 